// __UNIT_DOWNTIME__ (2026-10-08) the dated maintenance / out-of-service schedule of a truck or trailer
// (utils/equipmentDowntime.js; stored in Settings > Equipment rules). <UnitDowntimeEditor> sits in the
// Truck / Trailer Edit form, <DowntimePanel> in the Equipment Assignment view (every unit). On those days
// the job check warns (unit_restricted) and the hub's booking availability counts the unit as out.
// The Status field stays what it is NOW; this is for dates ahead.
import React, { useEffect, useState } from 'react';
import { fetchRules, updateRules } from '../../services/equipmentService';
import { DOWNTIME_KINDS, todayISO } from '../../utils/equipmentCompat';
import { downtimeOf, addDowntime, removeDowntime, downtimeText } from '../../utils/equipmentDowntime';

const inp = { padding: '0.35rem 0.45rem', backgroundColor: 'var(--surface-input, var(--bg-primary))', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: '5px', fontSize: '0.8rem' };
const btn = { padding: '0.35rem 0.7rem', backgroundColor: 'var(--surface-3)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: '5px', cursor: 'pointer', fontSize: '0.8rem' };

function useRules() {
  const [rules, setRules] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { let on = true; fetchRules(true).then((r) => { if (on) setRules(r); }).catch((e) => on && setErr(String(e.message || e))); return () => { on = false; }; }, []);
  return [rules, setRules, err, setErr];
}

function AddRow({ onAdd, busy, units }) {
  const [f, setF] = useState({ unit: '', start: todayISO(), end: '', kind: 'maintenance', note: '' });
  return (
    <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center', marginTop: '0.4rem' }}>
      {units && (
        <select value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} style={inp} aria-label="Unit">
          <option value="">Unit…</option>
          {units.map((u) => <option key={u.key} value={u.key}>{u.label}</option>)}
        </select>
      )}
      <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} style={inp} aria-label="Why">
        {Object.keys(DOWNTIME_KINDS).map((k) => <option key={k} value={k}>{DOWNTIME_KINDS[k]}</option>)}
      </select>
      <input type="date" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} style={inp} aria-label="First day" />
      <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>to</span>
      <input type="date" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} style={inp} aria-label="Last day (blank = until removed)" />
      <input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Note (safety, body shop...)" style={{ ...inp, minWidth: '160px' }} />
      <button type="button" disabled={busy} style={btn} onClick={() => onAdd(f, () => setF({ ...f, note: '' }))}>Add downtime</button>
    </div>
  );
}

export function UnitDowntimeEditor({ kind, unitId }) {
  const [rules, setRules, err, setErr] = useRules();
  const [busy, setBusy] = useState(false);
  if (!unitId) return null;
  const list = rules ? downtimeOf(rules, kind, unitId) : [];
  const change = async (fn) => {
    setBusy(true); setErr(null);
    try { setRules(await updateRules(fn)); } catch (e) { setErr(String(e.message || e)); } finally { setBusy(false); }
  };
  return (
    <div data-unit-downtime style={{ gridColumn: '1 / -1', border: '1px solid var(--border-default)', borderRadius: '6px', padding: '0.6rem' }}>
      <div style={{ fontWeight: 'bold', fontSize: '0.85rem', color: 'var(--text-primary)' }}>Downtime (maintenance / out of service)</div>
      <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)' }}>Dates this {kind} is off the road. The job check warns on them and online booking availability counts it as out. Saved right away (not with Update).</div>
      {!rules && !err && <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Loading…</div>}
      {list.length === 0 && rules && <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.3rem' }}>None planned.</div>}
      {list.map((x) => (
        <div key={x.id} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontSize: '0.8rem', marginTop: '0.25rem' }}>
          <span style={{ flex: 1 }}>{downtimeText(x)}</span>
          <button type="button" disabled={busy} style={btn} onClick={() => change((r) => removeDowntime(r, x.id))}>Remove</button>
        </div>
      ))}
      <AddRow busy={busy || !rules} onAdd={(f, done) => {
        const probe = addDowntime(rules, { unitKind: kind, unitId, start: f.start, end: f.end, kind: f.kind, note: f.note });
        if (probe.error) { setErr(probe.error); return; }
        change((r) => addDowntime(r, { unitKind: kind, unitId, start: f.start, end: f.end, kind: f.kind, note: f.note }).rules).then(done);
      }} />
      {err && <div style={{ color: 'var(--color-danger, #f85149)', fontSize: '0.78rem', marginTop: '0.3rem' }}>{err}</div>}
    </div>
  );
}

export function DowntimePanel({ trucks, trailers }) {
  const [rules, setRules, err, setErr] = useRules();
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const label = (x) => {
    const t = x.unit_kind === 'truck' ? (trucks || []).find((u) => String(u.id) === x.unit_id) : (trailers || []).find((u) => String(u.id) === x.unit_id);
    return t ? (t.truck_number || t.trailer_number || '?') : '(deleted ' + x.unit_kind + ')';
  };
  const today = todayISO();
  const list = rules ? downtimeOf(rules).filter((x) => !x.end || x.end >= today) : [];
  const units = [...(trucks || []).filter((t) => t.is_active !== false).map((t) => ({ key: 'truck:' + t.id, label: t.truck_number || '?' })),
    ...(trailers || []).filter((t) => t.is_active !== false).map((t) => ({ key: 'trailer:' + t.id, label: 'Trailer ' + (t.trailer_number || '?') }))];
  const change = async (fn) => {
    setBusy(true); setErr(null);
    try { setRules(await updateRules(fn)); } catch (e) { setErr(String(e.message || e)); } finally { setBusy(false); }
  };
  return (
    <div data-downtime-panel style={{ marginTop: '0.8rem', border: '1px solid var(--border-default)', borderRadius: '8px', padding: '0.6rem 0.8rem', backgroundColor: 'var(--surface-1)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer' }} onClick={() => setOpen(!open)}>
        <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '1rem' }}>Downtime schedule</h3>
        <span style={{ fontSize: '0.74rem', color: 'var(--text-muted)' }}>{rules ? list.length + ' planned (today and later)' : ''} - maintenance / out-of-service dates per unit</span>
        <span style={{ flex: 1 }} /><span>{open ? '▾' : '▸'}</span>
      </div>
      {open && (
        <div style={{ marginTop: '0.4rem' }}>
          {list.length === 0 && <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>None planned.</div>}
          {list.map((x) => (
            <div key={x.id} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontSize: '0.8rem', marginTop: '0.25rem' }}>
              <strong style={{ minWidth: '90px' }}>{label(x)}</strong><span style={{ flex: 1 }}>{downtimeText(x)}</span>
              <button type="button" disabled={busy} style={btn} onClick={() => change((r) => removeDowntime(r, x.id))}>Remove</button>
            </div>
          ))}
          <AddRow units={units} busy={busy || !rules} onAdd={(f, done) => {
            const [k, id] = String(f.unit || '').split(':');
            const probe = addDowntime(rules, { unitKind: k, unitId: id, start: f.start, end: f.end, kind: f.kind, note: f.note });
            if (probe.error) { setErr(probe.error); return; }
            change((r) => addDowntime(r, { unitKind: k, unitId: id, start: f.start, end: f.end, kind: f.kind, note: f.note }).rules).then(done);
          }} />
          {err && <div style={{ color: 'var(--color-danger, #f85149)', fontSize: '0.78rem', marginTop: '0.3rem' }}>{err}</div>}
        </div>
      )}
    </div>
  );
}
